import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { useToast } from '../../hooks/use-toast';
import MediaPicker from '../../components/MediaPicker';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const AdminBrandForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdit = Boolean(id);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [enabledCountries, setEnabledCountries] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  
  const [formData, setFormData] = useState({
    slug: '',
    logo: '',
    isActive: true,
    versions: []
  });

  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  useEffect(() => {
    fetchEnabledCountries();
  }, []);

  useEffect(() => {
    if (isEdit && enabledCountries.length > 0) {
      fetchBrand();
    } else if (!isEdit && enabledCountries.length > 0) {
      initializeFormForCreate();
    }
  }, [id, enabledCountries]);

  const fetchEnabledCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/localization/countries`);
      const data = await response.json();
      setEnabledCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const initializeFormForCreate = () => {
    const versions = enabledCountries.map(country => ({
      countryCode: country.code,
      language: country.language,
      name: '',
      description: '',
      isAvailable: true
    }));

    setFormData({
      slug: '',
      logo: '',
      isActive: true,
      versions: versions
    });
  };

  const fetchBrand = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/brands/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const brand = data.find(b => (b._id || b.id) === id);
      
      if (brand) {
        // Merge existing versions with newly enabled countries
        const existingVersions = brand.versions || [];
        const mergedVersions = enabledCountries.map(country => {
          const existingVersion = existingVersions.find(v => v.countryCode === country.code);
          
          if (existingVersion) {
            return existingVersion;
          } else {
            return {
              countryCode: country.code,
              language: country.language,
              name: '',
              description: '',
              isAvailable: true
            };
          }
        });
        
        setFormData({
          slug: brand.slug || '',
          logo: brand.logo || '',
          isActive: brand.isActive !== undefined ? brand.isActive : true,
          versions: mergedVersions
        });
      } else {
        toast({
          title: 'Error',
          description: 'Brand not found',
          variant: 'destructive'
        });
        navigate('/admin/brands');
      }
    } catch (error) {
      console.error('Error fetching brand:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch brand',
        variant: 'destructive'
      });
      navigate('/admin/brands');
    }
  };

  const updateVersionField = (versionIndex, field, value) => {
    const newVersions = [...formData.versions];
    newVersions[versionIndex] = {
      ...newVersions[versionIndex],
      [field]: value
    };
    setFormData({ ...formData, versions: newVersions });
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.slug) {
        toast({
          title: 'Validation Error',
          description: 'Slug is required',
          variant: 'destructive',
        });
        return;
      }

      const hasValidVersion = formData.versions.some(v => v.name && v.name.trim() !== '');
      if (!hasValidVersion) {
        toast({
          title: 'Validation Error',
          description: 'At least one country version with a name is required',
          variant: 'destructive',
        });
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('accessToken');
      let response;

      if (isEdit) {
        response = await fetch(`${API_URL}/api/brands/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${API_URL}/api/brands`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save brand');
      }

      toast({
        title: 'Success',
        description: isEdit ? 'Brand updated successfully' : 'Brand created successfully',
      });

      navigate('/admin/brands');
    } catch (error) {
      console.error('Error saving brand:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save brand',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (imageUrl) => {
    setFormData({ ...formData, logo: imageUrl });
    setShowMediaPicker(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/admin/brands')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Brand' : 'Add Brand'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEdit ? 'Update brand details and country versions' : 'Create a new brand with country-specific versions'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Master Brand Information */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold mb-4">Master Brand Information</h3>
          
          <div className="space-y-4">
            {/* Slug */}
            <div>
              <Label>Slug (URL) *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="brand-name"
              />
              <p className="text-xs text-gray-500 mt-1">URL-friendly identifier</p>
            </div>

            {/* Logo */}
            <div>
              <Label>Brand Logo</Label>
              <div className="mt-2 space-y-2">
                {formData.logo && (
                  <div className="relative inline-block">
                    <img
                      src={resolveMediaUrl(formData.logo)}
                      alt="Brand logo"
                      className="w-32 h-32 object-contain rounded border p-2"
                      onError={(e) => { e.currentTarget.src = 'https://placehold.co/128x128?text=No+logo'; }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0"
                      onClick={() => setFormData({ ...formData, logo: '' })}
                    >
                      ×
                    </Button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMediaPicker(true)}
                  className="w-full"
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  {formData.logo ? 'Change Logo' : 'Select Logo'}
                </Button>
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label className="font-normal cursor-pointer">Active Brand</Label>
            </div>
          </div>
        </div>

        {/* Country Versions Accordion */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Country Versions</h3>
          <p className="text-sm text-gray-600 mb-4">
            Create versions for each country. At least one version with a name is required.
          </p>

          <Accordion type="multiple" value={expandedAccordions} onValueChange={setExpandedAccordions}>
            {formData.versions.map((version, versionIndex) => {
              const country = enabledCountries.find(c => c.code === version.countryCode);
              if (!country) return null;

              return (
                <AccordionItem key={versionIndex} value={`country-${versionIndex}`} className="border rounded-lg mb-2">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFlagEmoji(country.code)}</span>
                      <div className="text-left">
                        <div className="font-semibold">{country.name}</div>
                        <div className="text-sm text-gray-500">
                          {country.languageName}
                          {version.name && <span className="ml-2 text-blue-600">• {version.name}</span>}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4 pt-4">
                      {/* Brand Name */}
                      <div>
                        <Label>Brand Name ({country.languageName}) *</Label>
                        <Input
                          value={version.name}
                          onChange={(e) => {
                            updateVersionField(versionIndex, 'name', e.target.value);
                            if (!isEdit && versionIndex === 0 && !formData.slug) {
                              setFormData({ ...formData, slug: generateSlug(e.target.value) });
                            }
                          }}
                          placeholder={`Enter brand name in ${country.languageName}`}
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <Label>Description ({country.languageName})</Label>
                        <Textarea
                          value={version.description}
                          onChange={(e) => updateVersionField(versionIndex, 'description', e.target.value)}
                          rows={4}
                          placeholder="Brand description"
                        />
                      </div>

                      {/* Availability */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={version.isAvailable}
                          onCheckedChange={(checked) => updateVersionField(versionIndex, 'isAvailable', checked)}
                        />
                        <Label className="font-normal cursor-pointer">
                          ✓ Available in {country.name}
                        </Label>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end pt-6 border-t">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/brands')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#be1f1a] hover:bg-[#a71b17]"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Brand' : 'Create Brand')}
          </Button>
        </div>
      </div>

      {/* Media Picker */}
      <MediaPicker
        isOpen={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleImageSelect}
        fileType="image"
      />
    </div>
  );
};

export default AdminBrandForm;
